export const ACQUISITION_STORAGE_KEY = 'focuscorp_paid_acquisition';
export const ACQUISITION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const REDIRECT_STORAGE_KEY = 'focuscorp_acquisition_redirect_v1';
const REDIRECT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
export const ACQUISITION_QUERY_KEYS = [
  'qz', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
  'campaign_id', 'adset_id', 'ad_id', 'fbclid', 'gclid',
  'af_c_id', 'af_ad_id', 'af_adset_id', 'af_channel', 'af_sub1', 'af_sub2',
] as const;
const CURRENT_PROPERTY_KEYS = [...ACQUISITION_QUERY_KEYS, 'landing_path', 'referrer_origin', 'captured_at'];

type Properties = Record<string, string>;
type Storage = Pick<globalThis.Storage, 'getItem' | 'setItem' | 'removeItem'>;
type Environment = {
  getLocation: () => { pathname: string; search: string };
  getReferrer: () => string;
  hasConsent: () => boolean;
  getLocalStorage: () => Storage;
  getSessionStorage: () => Storage;
  now?: () => number;
  isTestEntry?: () => boolean;
};

function isPaid(properties: Properties) {
  return ['fc1', 'fcga1'].includes(properties.qz ?? '') ||
    Boolean(properties.gclid || properties.campaign_id || properties.adset_id || properties.ad_id ||
      properties.af_c_id || properties.af_ad_id || properties.af_adset_id) ||
    /^(cpc|ppc|paid|paid[_ -]?social|paid[_ -]?search|cpm|display|retargeting)$/iu.test(properties.utm_medium ?? '') ||
    Boolean(properties.fbclid && properties.utm_source);
}

// One tracker represents one document entry. SPA navigation does not create a
// new click; only an explicit auth redirect may resume this entry in a new document.
export function createAcquisitionTracker(environment: Environment) {
  const now = environment.now ?? Date.now;
  function snapshotLocation() {
    const { pathname, search } = environment.getLocation();
    return { pathname, search: pathname === '/auth/callback' ? '' : search };
  }
  let entry = snapshotLocation();
  let entryReferrer = environment.getReferrer();
  let entryCapturedAt = now();
  let current: Properties | null = null;
  let redirectChecked = false;
  const isTestEntry = () => environment.isTestEntry?.() === true ||
    new URLSearchParams(entry.search).get('qz') === 'tst1';

  function remove(getStorage: () => Storage, key: string) {
    try { getStorage().removeItem(key); } catch { /* Private browsing may block storage. */ }
  }

  function read(getStorage: () => Storage, key: string, maxAge: number): Properties | null {
    try {
      const parsed: unknown = JSON.parse(getStorage().getItem(key) ?? 'null');
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        const properties = Object.fromEntries(Object.entries(parsed).filter(
          ([name, value]) => CURRENT_PROPERTY_KEYS.includes(name as typeof CURRENT_PROPERTY_KEYS[number]) && typeof value === 'string',
        )) as Properties;
        const capturedAt = Date.parse(properties.captured_at ?? '');
        const age = now() - capturedAt;
        if (Number.isFinite(capturedAt) && age >= 0 && age < maxAge && properties.landing_path) return properties;
      }
    } catch { /* Invalid or unavailable storage cannot supply attribution. */ }
    remove(getStorage, key);
    return null;
  }

  function getCurrentProperties(): Properties {
    if (isTestEntry()) {
      // Operational checkout metadata only. Never read or replace a real paid
      // click, and discard any ad identifiers copied into a test URL.
      return { qz: 'tst1', utm_source: 'internal_test', landing_path: '/start?qz=tst1', referrer_origin: 'direct' };
    }
    if (!environment.hasConsent()) {
      return { landing_path: environment.getLocation().pathname, referrer_origin: 'direct' };
    }
    if (current) return { ...current };

    if (!redirectChecked) {
      redirectChecked = true;
      if (entry.pathname === '/auth/callback') {
        current = read(environment.getSessionStorage, REDIRECT_STORAGE_KEY, REDIRECT_MAX_AGE_MS);
      }
      remove(environment.getSessionStorage, REDIRECT_STORAGE_KEY);
      if (current) return { ...current };
    }

    current = {
      landing_path: `${entry.pathname}${entry.search}`,
      referrer_origin: 'direct',
      captured_at: new Date(entryCapturedAt).toISOString(),
    };
    if (entryReferrer) {
      try { current.referrer_origin = new URL(entryReferrer).origin; } catch { /* Do not retain malformed referrer contents. */ }
    }
    const params = new URLSearchParams(entry.search);
    for (const key of ACQUISITION_QUERY_KEYS) {
      const value = params.get(key)?.trim();
      if (value) current[key] = value;
    }
    if (isPaid(current)) {
      try { environment.getLocalStorage().setItem(ACQUISITION_STORAGE_KEY, JSON.stringify(current)); } catch { /* Current entry remains usable without storage. */ }
    }
    return { ...current };
  }

  function getCheckoutProperties(): Properties {
    const observed = getCurrentProperties();
    if (isTestEntry() || !environment.hasConsent()) return observed;
    // Historical context is used only for conversion attribution, never as a
    // page view or a new acquisition. Reading cannot extend the 30-day window.
    const saved = read(environment.getLocalStorage, ACQUISITION_STORAGE_KEY, ACQUISITION_MAX_AGE_MS);
    if (saved && isPaid(saved)) return saved;
    if (saved) remove(environment.getLocalStorage, ACQUISITION_STORAGE_KEY);
    if (isPaid(observed) && now() - Date.parse(observed.captured_at ?? '') >= ACQUISITION_MAX_AGE_MS) {
      return { landing_path: environment.getLocation().pathname, referrer_origin: 'direct' };
    }
    return observed;
  }

  function preserveForRedirect() {
    if (isTestEntry()) return;
    if (!environment.hasConsent()) return;
    try { environment.getSessionStorage().setItem(REDIRECT_STORAGE_KEY, JSON.stringify(getCurrentProperties())); } catch { /* Checkout can still use local last-paid context. */ }
  }

  function clear() {
    remove(environment.getLocalStorage, ACQUISITION_STORAGE_KEY);
    remove(environment.getSessionStorage, REDIRECT_STORAGE_KEY);
    current = null;
    redirectChecked = true;
    entry = snapshotLocation();
    entryReferrer = '';
    entryCapturedAt = now();
  }

  function sanitizeEventProperties(properties: Record<string, unknown>) {
    const clean = { ...properties };
    // Override old register_once properties, including the automatic initial
    // page view that PostHog emits during init, before register/unregister runs.
    for (const key of CURRENT_PROPERTY_KEYS) delete clean[key];
    return { ...clean, ...getCurrentProperties(), attribution_version: '2' };
  }

  return { getCurrentProperties, getCheckoutProperties, preserveForRedirect, clear, sanitizeEventProperties };
}
